import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useHistory } from 'react-router-dom'
import { IconExternalLink } from '@tabler/icons-react'

import { patchTask } from '@/reducers/tasks/tasks.actions'
import {
  IGNORED,
  READY_FOR_REVIEW,
  IN_PROGRESS,
  COLLECTING_EVIDENCE,
} from '@/reducers/tasks/tasks.constants'
import { setTask } from '@/reducers/tasks/tasks.redux'
import { resetMediaPlayer } from '@/reducers/media/media.redux'
import { getScorecardLookupData } from '@/utils/promptFeedback.helpers'
import { TaskComments } from '../../components/TaskComments'
import { TaskDrawerFooter } from '../TaskDrawerFooter'
import { Metadata } from '../Metadata'
import { EvidenceList } from '../prompt-feedback/components/EvidenceList'

import './CustomerFeedbackDrawer.scss'

export const CustomerFeedbackDrawer = ({ refetchTasks }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { task } = useSelector((state) => state.tasks)
  const { scorecardConfigs } = useSelector((state) => state.scorecards.data)

  const allEvidence = task?.evidence || []
  const evidenceCount = allEvidence.length
  const uniqueCallCount = new Set(allEvidence.map((evidence) => evidence.call_id)).size
  const expectedScore = task?.expected_score
  const feedbackText = task?.description

  const { matchingScorecardConfig, scorecardConfigId } = getScorecardLookupData(
    allEvidence,
    scorecardConfigs
  )

  const agent = `${task?.agent_first_name} ${task?.agent_last_name}`
  const createdBy = task?.created_by
    ? `${task?.created_by_first_name} ${task?.created_by_last_name}`
    : null

  const isTaskOpen = [READY_FOR_REVIEW, IN_PROGRESS, COLLECTING_EVIDENCE].includes(task?.status)

  const closeDrawer = () => {
    dispatch(setTask(null))
    dispatch(resetMediaPlayer())
    history.push({})
  }

  const handleWithdrawFeedback = async () => {
    await dispatch(patchTask(task.id, { status: IGNORED }))
    if (refetchTasks) {
      refetchTasks()
    }
    closeDrawer()
  }

  return (
    <div className="customer-feedback-drawer" data-testid="customer-feedback-drawer">
      <h2>{task?.name}</h2>

      <Metadata
        agent={agent}
        createdAt={task?.created_at}
        createdBy={createdBy}
        isAiGenerated={task?.ai_generated}
        type={task?.type}
        status={task?.status}
        agentReviewed={task?.agent_reviewed}
      />

      <div className="customer-feedback-drawer__content">
        <section className="customer-feedback-drawer__section">
          <h3>
            Feedback Details
            {evidenceCount > 1 && (
              <span className="muted-text evidence-count">
                ({evidenceCount} reports from {uniqueCallCount} call
                {uniqueCallCount !== 1 ? 's' : ''})
              </span>
            )}
          </h3>

          {(matchingScorecardConfig || scorecardConfigId) && (
            <div className="reference-links mb">
              <div className="reference-item">
                <span className="muted-text">Scorecard: </span>
                <Link
                  to={`/qa-copilot/${matchingScorecardConfig?.id || scorecardConfigId}`}
                  target="_blank"
                  className="text-button flex-align-center small-gap"
                >
                  <strong>{matchingScorecardConfig?.name || `ID: ${scorecardConfigId}`}</strong>
                  <IconExternalLink size={16} />
                </Link>
              </div>
            </div>
          )}

          <EvidenceList
            allEvidence={allEvidence}
            expectedScore={expectedScore}
            feedbackText={feedbackText}
            prompts={[]}
            showPromptStatus={false}
          />
        </section>

        <TaskDrawerFooter
          handlePrimaryAction={closeDrawer}
          primaryActionButtonText="Close"
          handleSecondaryAction={isTaskOpen ? handleWithdrawFeedback : null}
          secondaryActionButtonText="Withdraw Feedback"
        />
      </div>

      <div className="customer-feedback-drawer__comments">
        <h3>Notes</h3>
        <TaskComments
          taskId={task?.id}
          organizationId={task?.organization_id}
          taskType={task?.type}
        />
      </div>
    </div>
  )
}
